import { z } from 'zod';
import { languageCodeSchema } from './facility';

// Per-organization terms-and-conditions catalog
// (organizations.terms_and_conditions). Drives the T&C dropdown on the
// itinerary settings page. The DB row also carries a multilingual jsonb
// `description` exposed by the admin editor surface (see detail schema
// below).
export const termsAndConditionsItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
});

export type TermsAndConditionsItem = z.infer<
  typeof termsAndConditionsItemSchema
>;

export const findTermsAndConditionsResultSchema = z.object({
  data: z.array(termsAndConditionsItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindTermsAndConditionsResult = z.infer<
  typeof findTermsAndConditionsResultSchema
>;

// Output schema for the `description` jsonb entries. Permissive on
// `languageCode` because the DB may hold legacy codes outside the
// current language enum; the write schema below enforces the enum.
const termsAndConditionsDescriptionEntryOutputSchema = z.object({
  languageCode: z.string(),
  text: z.string(),
});

export const termsAndConditionsDetailSchema =
  termsAndConditionsItemSchema.extend({
    description: z.array(termsAndConditionsDescriptionEntryOutputSchema),
  });

export type TermsAndConditionsDetail = z.infer<
  typeof termsAndConditionsDetailSchema
>;

// Write-side validation: strict language enum + non-empty rich-text HTML.
const termsAndConditionsDescriptionEntryInputSchema = z.object({
  languageCode: languageCodeSchema,
  text: z.string().min(3),
});

export const createTermsAndConditionsInput = z.object({
  title: z.string().min(3),
  description: z.array(termsAndConditionsDescriptionEntryInputSchema).min(1),
});

export type CreateTermsAndConditionsInput = z.infer<
  typeof createTermsAndConditionsInput
>;

export const updateTermsAndConditionsInput = z.object({
  id: z.uuid(),
  title: z.string().min(3).optional(),
  description: z
    .array(termsAndConditionsDescriptionEntryInputSchema)
    .min(1)
    .optional(),
});

export type UpdateTermsAndConditionsInput = z.infer<
  typeof updateTermsAndConditionsInput
>;

export const deleteTermsAndConditionsInput = z.object({ id: z.uuid() });

export type DeleteTermsAndConditionsInput = z.infer<
  typeof deleteTermsAndConditionsInput
>;

export const deleteTermsAndConditionsResultSchema = z.object({
  id: z.uuid(),
});

export type DeleteTermsAndConditionsResult = z.infer<
  typeof deleteTermsAndConditionsResultSchema
>;

// Per-language "translate from source" on the T&C editor. Mirrors the
// products edit-tab control, but T&C has no title — only the multilingual
// HTML `description`. The caller supplies the source-language HTML the form
// already holds (no DB read), so unsaved content translates too.
export const translateTermsAndConditionsInput = z
  .object({
    sourceLanguage: languageCodeSchema,
    targetLanguage: languageCodeSchema,
    description: z.string(),
  })
  .refine((input) => input.sourceLanguage !== input.targetLanguage, {
    message: 'Source and target languages must differ.',
  });

export type TranslateTermsAndConditionsInput = z.infer<
  typeof translateTermsAndConditionsInput
>;

export const translateTermsAndConditionsResult = z.object({
  description: z.string(),
});

export type TranslateTermsAndConditionsResult = z.infer<
  typeof translateTermsAndConditionsResult
>;
